import { t } from '@citizen-walk/shared-i18n';
import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useState } from 'react';
import { useAdminAuth } from './useAdminAuth';

/**
 * Admin app の sign-in 画面 (Sprint admin 第 9 弾、Phase 5.4)。
 *
 * admin 第 24 弾 (2026-05-19): Google OAuth (signInWithPopup) 追加。旧 LoginPage.tsx 踏襲、
 * prompt: select_account でアカウント切替明示、popup-closed/cancelled は silent。
 * Email + Password はそのまま継続維持 (master 初期 sign-in 用)、Emulator では
 * master@example.com / masterpass で sign-in 可能。
 */
export function SignInPage() {
  const { signIn, signInWithGoogle, error } = useAdminAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast('warning', 'メールとパスワードを入力してください');
      return;
    }
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      toast('success', 'サインインしました');
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-12">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900">CitizenWalk Admin</h1>
        <p className="text-sm text-slate-500 mt-1">管理者サインイン</p>
      </header>

      <Card padding="lg">
        {/* admin 第 24 弾: Google OAuth button (旧 LoginPage 踏襲)。
            popup-closed-by-user は silent、unauthorized-domain は明示メッセージ。 */}
        <button
          type="button"
          onClick={async () => {
            setGoogleSubmitting(true);
            try {
              await signInWithGoogle();
              toast('success', 'Google でサインインしました');
            } catch (err) {
              toast('error', (err as Error).message);
            } finally {
              setGoogleSubmitting(false);
            }
          }}
          disabled={submitting || googleSubmitting}
          className="w-full flex items-center justify-center gap-3 min-h-touch px-4 py-3 border border-slate-300 rounded-md bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors mb-6"
        >
          <GoogleIcon />
          <span className="text-sm font-medium text-slate-700">
            {googleSubmitting ? 'サインイン中…' : 'Google でサインイン'}
          </span>
        </button>

        <div className="flex items-center gap-3 my-4 text-[11px] text-slate-400">
          <span className="flex-1 h-px bg-slate-200" />
          <span>または</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">メールアドレス</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
              placeholder="master@example.com"
              autoComplete="email"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">パスワード</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm"
              autoComplete="current-password"
              required
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded p-2 border border-red-200">
              {error.message}
            </p>
          )}

          <Button
            variant="primary"
            size="md"
            type="submit"
            disabled={submitting}
            className="w-full"
          >
            {/* Sprint BG Part 3 — i18n 化 (ja default "サインイン")。 */}
            {submitting ? 'サインイン中…' : t('auth.signin_button')}
          </Button>
        </form>

        {/* Option C (docs/110): セルフのパスワード再発行は廃止し、マスター発行に一本化。
            理由 = 再発行メールの着地ドメインをコードで是正できない (callbackUri が
            EMAIL_TEMPLATE_UPDATE_NOT_ALLOWED でロック)。マスターの再発行リンク (/auth/invite) は
            カスタムドメイン + 期限指定 + 自動ログインで動くため、そちらに寄せる。 */}
        <p className="mt-3 text-center text-xs text-slate-500">
          パスワードをお忘れの場合は、マスター管理者に再発行（リンクの発行）を依頼してください。
        </p>

        <p className="mt-4 text-[10px] text-slate-400">
          ※ Emulator では <code>master@example.com</code> / <code>masterpass</code> で sign-in 可能
          (seed-emulator.mjs で設定)。本番では Google OAuth 推奨。
        </p>
      </Card>
    </div>
  );
}

/** Google brand 4 色 SVG icon (旧 LoginPage.tsx 踏襲)。 */
function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
